import { AlertTriangle, RefreshCw } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════
// VERSION BANNER
// Shows a warning when client/API versions are incompatible
// ═══════════════════════════════════════════════════════════════════════════

export interface VersionBannerProps {
  status: "compatible" | "incompatible" | "error" | "loading";
  message?: string;
  clientVersion: string;
  apiVersion?: string;
  onDismiss?: () => void;
  onRefresh?: () => void;
}

export function VersionBanner({
  status,
  message,
  clientVersion,
  apiVersion,
  onDismiss,
  onRefresh,
}: VersionBannerProps) {
  if (status === "compatible" || status === "loading") return null;

  const isIncompatible = status === "incompatible";

  return (
    <div
      className={`fixed top-0 inset-x-0 z-[9999] px-4 py-3 text-sm font-medium shadow-md ${
        isIncompatible ? "bg-red-600 text-white" : "bg-amber-500 text-amber-950"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle size={16} className="shrink-0" />
          <span>
            {isIncompatible
              ? message || "Version mismatch — please update"
              : "Could not verify version compatibility"}
          </span>
          {apiVersion && (
            <span className="opacity-70 text-xs">
              (App {clientVersion} / API {apiVersion})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="rounded px-2 py-1 text-xs hover:bg-white/20 transition-colors"
              title="Retry version check"
            >
              <RefreshCw size={14} />
            </button>
          )}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="rounded px-2 py-1 text-xs hover:bg-white/20 transition-colors"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
